const GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/geo.json';
const GEO_STORAGE_KEY = 'banner-carousel-geo-state';
const UTM_STORAGE_KEY = 'banner-carousel-utm';

const BR_STATE_BY_NAME = {
  'acre': 'ac',
  'alagoas': 'al',
  'amapa': 'ap',
  'amazonas': 'am',
  'bahia': 'ba',
  'ceara': 'ce',
  'distrito federal': 'df',
  'espirito santo': 'es',
  'goias': 'go',
  'maranhao': 'ma',
  'mato grosso': 'mt',
  'mato grosso do sul': 'ms',
  'minas gerais': 'mg',
  'para': 'pa',
  'paraiba': 'pb',
  'parana': 'pr',
  'pernambuco': 'pe',
  'piaui': 'pi',
  'rio de janeiro': 'rj',
  'rio grande do norte': 'rn',
  'rio grande do sul': 'rs',
  'rondonia': 'ro',
  'roraima': 'rr',
  'santa catarina': 'sc',
  'sao paulo': 'sp',
  'sergipe': 'se',
  'tocantins': 'to'
};

function normalizeText(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function toStateCode(value) {
  const normalized = normalizeText(value);

  return BR_STATE_BY_NAME[normalized] || normalized;
}

function currentUtmParams() {
  const stored = JSON.parse(sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');

  for (const [key, value] of new URLSearchParams(window.location.search)) {
    if (key.startsWith('utm_')) {
      stored[key] = value;
    }
  }

  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(stored));

  return stored;
}

let visitorStatePromise = null;

function visitorState() {
  if (!visitorStatePromise) {
    const cached = sessionStorage.getItem(GEO_STORAGE_KEY);

    if (cached !== null) {
      visitorStatePromise = Promise.resolve(cached);
    } else {
      visitorStatePromise = fetch(GEO_ENDPOINT)
        .then((response) => response.json())
        .then((data) => {
          const state = toStateCode(data.region || '');

          sessionStorage.setItem(GEO_STORAGE_KEY, state);

          return state;
        })
        .catch(() => '');
    }
  }

  return visitorStatePromise;
}

class BannerCarousel extends HTMLElement {
  constructor() {
    super();

    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', () => {
        this.init();
      });
    } else {
      this.init();
    }
  }

  async init() {
    try {
      await this.applyDynamicFilters();
    } finally {
      this.querySelector('.banner-skeleton')?.remove();
    }

    if (this.querySelector('.swiper-slide')) {
      this.initSwiper();
    } else {
      const wrapper = this.closest('.section-margin-bottom') || this;

      wrapper.style.display = 'none';
    }
  }

  async applyDynamicFilters() {
    const dynamicSlides = this.querySelectorAll('.swiper-slide[data-geo-states], .swiper-slide[data-utm-param]');

    if (dynamicSlides.length === 0) {
      return;
    }

    const utmParams = currentUtmParams();
    const state = this.querySelector('.swiper-slide[data-geo-states]') ? await this.resolveState() : '';

    dynamicSlides.forEach((slide) => {
      if (!this.slideMatches(slide, state, utmParams)) {
        slide.remove();
      }
    });
  }

  resolveState() {
    const customerState = toStateCode(this.dataset.customerState || '');

    if (customerState) {
      return Promise.resolve(customerState);
    }

    return visitorState();
  }

  slideMatches(slide, state, utmParams) {
    const { geoStates, utmParam, utmValues } = slide.dataset;

    if (geoStates) {
      const acceptedStates = geoStates.split(',').map(toStateCode);

      if (!state || !acceptedStates.includes(state)) {
        return false;
      }
    }

    if (utmParam) {
      const acceptedValues = utmValues.split(',').map(normalizeText);
      const currentValue = utmParams[utmParam];

      if (!currentValue || !acceptedValues.includes(normalizeText(currentValue))) {
        return false;
      }
    }

    return true;
  }

  initSwiper() {
    const slideCount = this.querySelectorAll('.swiper-slide').length;

    if (slideCount < 2) {
      this.querySelector('.swiper-custom-navigation')?.remove();
    }

    new Swiper(this.querySelector('.swiper'), {
      slidesPerView: 1,
      effect: 'fade',
      crossFade: true,
      loop: slideCount > 1,
      speed: 1000,
      autoplay: {
        delay: 5000,
        pauseOnMouseEnter: true
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-custom-next'),
        prevEl: this.querySelector('.swiper-button-custom-prev'),
      },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true,
      },
    });
  }
}

customElements.define('banner-carousel', BannerCarousel);
